import React from 'react';
import { call, takeEvery } from 'redux-saga/effects';
import { SystemTenantForm, mountForm, unmountForm } from '@kineticdata/react';
import { generateFormButtons, FormError } from '@kineticdata/ui';
import { regRoute, regSaga } from '../../store';
import { navigate } from '../../root';
import { Link } from 'react-router-dom';
import {
  DATABASE_ADAPTER_FIELDS,
  MssqlPropertyLayout,
  OraclePropertyLayout,
  PostgresPropertyLayout,
} from '../common/DatabaseAdapters';

const formKey = 'tenant-new';

const handleSave = () => ({ tenant }) => {
  if (tenant) {
    // If we got a `tenant` then this was a create and we redirect to the edit.
    navigate(`/spaces/edit/${tenant.slug}`);
  }
};

regRoute('TENANT_NEW_PAGE', '/spaces/new');

regSaga(
  takeEvery('ENTER_TENANT_NEW_PAGE', function*() {
    yield call(mountForm, formKey);
  }),
);

regSaga(
  takeEvery('LEAVE_TENANT_NEW_PAGE', function*() {
    yield call(unmountForm, formKey);
  }),
);

const FormLayout = ({ fields, error, buttons, meta, bindings }) => {
  const currentAdapter = bindings.values.get('task_databaseAdapter_type');

  return (
    <form>
      <div className="row">
        <div className="col-6">{fields.get('name')}</div>
        <div className="col-6">{fields.get('slug')}</div>
        <div className="col-12">{fields.get('imageToggle')}</div>
        <div className="col-12">{fields.get('image')}</div>
      </div>

      <div className="heading heading-sm">
        <h2 className="h3">Admin User</h2>
      </div>
      <div className="row">
        <div className="col-6">{fields.get('email')}</div>
        <div className="col-6">{fields.get('username')}</div>
        <div className="col-12">{fields.get('displayName')}</div>
        <div className="col-12">
          {meta.getIn(['password', 'visible']) ? (
            <fieldset>
              <legend className="visually-hidden">Change password</legend>

              {fields.get('password')}
              {fields.get('passwordConfirmation')}
              {fields.get('changePassword')}
            </fieldset>
          ) : (
            <div className="form-group">{fields.get('changePassword')}</div>
          )}
        </div>
      </div>

      <div className="heading heading-sm">
        <h2 className="h3">Task Database</h2>
      </div>

      <div className="row">
        <div className="col-12">{fields.get('task_databaseAdapter_type')}</div>
        <div className="col-12">{fields.get('task_autoCreateDatabase')}</div>
        {currentAdapter === 'mssql' ? (
          <MssqlPropertyLayout fields={fields} values={bindings.values} />
        ) : currentAdapter === 'oracle' ? (
          <OraclePropertyLayout fields={fields} values={bindings.values} />
        ) : currentAdapter === 'postgres' &&
        !bindings.values.get('task_autoCreateDatabase') ? (
          <PostgresPropertyLayout fields={fields} values={bindings.values} />
        ) : null}
      </div>

      {error}
      <div className="form-buttons">{buttons}</div>
    </form>
  );
};

export const SpaceNew = props => {
  return (
    <div className="app-content__main">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/spaces">Spaces</Link>
          </li>
          <li className="breadcrumb-item active">New Space</li>
        </ol>
      </nav>
      <div className="heading">
        <h1>New Space</h1>
      </div>
      <div className="content-container">
        <SystemTenantForm
          formKey={formKey}
          onSave={handleSave}
          components={{
            FormError,
            FormButtons: generateFormButtons({
              submitLabel: 'Create Space',
              cancelPath: '/spaces',
            }),
            FormLayout,
          }}
          addFields={() => () => [
            {
              name: 'imageToggle',
              label: 'Use Default Task Image',
              type: 'checkbox',
              transient: true,
              initialValue: true,
              onChange: ({ values }, { setValue }) => {
                if (!!values.get('imageToggle')) {
                  setValue('image', '');
                }
              },
            },
          ]}
          alterFields={{
            ...DATABASE_ADAPTER_FIELDS(),
            image: {
              visible: ({ values }) => !values.get('imageToggle'),
              helpText:
                'Optionally set the version of Kinetic Task to use for this space. Leave blank to use the default version.',
              renderAttributes: { inlineHelpText: true },
            },
          }}
        />
      </div>
    </div>
  );
};
